"use client";

import { useEffect, useState } from "react";
import {
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  INPUT_MONO,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  CrackTable,
  guessText,
  PatternList,
  Suggestions,
} from "@/components/tools/cipher-machines/password-report";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildDictionaries,
  type CharacterPools,
  estimateStrength,
  MAX_LENGTH,
  type PasswordDictionaries,
  suggestionsFor,
} from "@/lib/tool-kit/ciphers/password-strength";

const RATING_TONES = [
  "bg-red-500",
  "bg-orange-500",
  "bg-amber-400",
  "bg-lime-500",
  "bg-green-600",
] as const;

const POOL_KEYS = ["lower", "upper", "digits", "symbols", "other"] as const;

/** English strings. A translated content file overrides these through `ui`. */
export const PASSWORD_STRENGTH_CHECKER_STRINGS = {
  privacyTitle: "Checked on your device — nothing is sent",
  privacy:
    "Your password never leaves this page: it is not sent to CitationEasy or anyone else, not stored and not logged. The word lists it is compared with are downloaded to your browser, and the check runs there.",
  password: "Password to check",
  placeholder: "Type or paste a password",
  show: "Show",
  hide: "Hide",
  showLabel: "Show the password",
  hideLabel: "Hide the password",
  loading: "Loading the word lists…",
  loadFailed:
    "The word lists could not be loaded, so the check cannot run. Reload the page to try again.",
  empty: "Type a password to see how quickly it could be guessed.",
  truncated: "Only the first {count} characters are checked.",
  verdict: "Verdict",
  rating0: "Very weak",
  rating1: "Weak",
  rating2: "Fair",
  rating3: "Strong",
  rating4: "Very strong",
  rating0Note:
    "Guessable within a year even by an attacker held to 100 tries an hour.",
  rating1Note:
    "Holds out against throttled online guessing, but not against an attacker allowed 10 tries a second.",
  rating2Note:
    "Safe from online guessing, but not for long if a site's password database is stolen.",
  rating3Note:
    "Holds out for a year against a stolen database protected by a slow hash such as bcrypt — but not against a fast hash such as MD5.",
  rating4Note:
    "Holds out for over a year even against fast offline cracking of an unsalted MD5 hash.",
  meterLabel: "Strength {rating} of 4: {label}",
  length: "Characters",
  pools: "Character types",
  poolLower: "a–z",
  poolUpper: "A–Z",
  poolDigits: "0–9",
  poolSymbols: "symbols",
  poolOther: "other",
  poolNone: "none",
  poolBits: "Character-set entropy",
  patternBits: "Pattern-aware estimate",
  bits: "one: {count} bit|other: {count} bits",
  guesses: "Estimated guesses",
  entropyNote:
    "Character-set entropy is length × log₂(possible characters): the strength only if every character had been picked at random. The pattern-aware estimate counts the guesses needed when the attacker tries likely passwords first — the number to trust.",
  crackHeading: "Time to guess it",
  attackColumn: "Attack",
  speedColumn: "Guesses",
  timeColumn: "Time",
  onlineThrottled: "Online, throttled — a site that limits failed logins",
  onlineUnthrottled: "Online, not throttled",
  offlineSlow: "Stolen database, slow hash (bcrypt, scrypt, Argon2)",
  offlineFast: "Stolen database, fast unsalted hash (MD5) on one graphics card",
  perHour: "{count} an hour",
  perSecond: "{count} a second",
  instant: "less than a second",
  eons: "more than a billion years",
  crackNote:
    "Assumes the attacker tries guesses in roughly this checker's order — common passwords, then words, names and patterns, then random strings — and needs about the estimated number of guesses. Real cracking lists are far longer than these, so read a good result as a best case.",
  patternsHeading: "What the estimate found",
  matchPassword: "a common password — number {rank} on the list",
  matchEnglish: "the English word “{word}”",
  matchName: "the first name “{word}”",
  matchSurname: "the surname “{word}”",
  matchWordlike:
    "“{word}” is on no list, but its letters read like English — priced by letter-pair odds",
  matchL33t: "with look-alike swaps",
  matchReversed: "written backwards",
  matchCapitals: "with capitals",
  matchSequence: "a sequence",
  matchRepeat: "one: “{unit}” once|other: “{unit}” repeated {count} times",
  matchKeyboard: "a keyboard pattern",
  matchYear: "a year",
  matchDate: "a date",
  matchRandom: "no pattern: priced as random characters",
  matchGuesses: "≈ {guesses} guesses",
  suggestionsHeading: "How to make it stronger",
  suggestCommon:
    "It is on a list of the most common passwords, which attackers try first. Do not use it anywhere.",
  suggestCommonPart:
    "Part of it is on the list of the most common passwords. Attackers try those first, with the usual digits and symbols added on.",
  suggestWords:
    "Words and names are in every cracking dictionary. Four or more random, unrelated words — or a password manager's generator — are far stronger.",
  suggestL33t:
    "Swapping @ for a or 0 for o does little: crackers try those swaps automatically.",
  suggestCapitals:
    "A capital at the start or the end is the first variation crackers try.",
  suggestReversed: "Words written backwards are tried too.",
  suggestSequence: "Avoid runs such as abc, 1234 or 2468.",
  suggestRepeat:
    "Avoid repeated characters and repeated chunks such as abcabc.",
  suggestKeyboard: "Avoid keyboard patterns such as qwerty, asdf or 1qaz2wsx.",
  suggestDate:
    "Avoid years and dates — birthdays and anniversaries are easy to find or guess.",
  suggestLength:
    "Make it longer. Current NIST guidance (SP 800-63B-4, 2025) sets 15 characters as the minimum for a password used on its own, and length adds more than symbols do.",
  suggestGood:
    "No weakness found. Use it for this one account only, and keep it in a password manager.",
} as const;

export type PasswordStrings = typeof PASSWORD_STRENGTH_CHECKER_STRINGS;

const RATING_KEYS = [
  "rating0",
  "rating1",
  "rating2",
  "rating3",
  "rating4",
] as const;
const RATING_NOTES = [
  "rating0Note",
  "rating1Note",
  "rating2Note",
  "rating3Note",
  "rating4Note",
] as const;

function poolText(pools: CharacterPools, strings: PasswordStrings): string {
  const names: Record<(typeof POOL_KEYS)[number], string> = {
    lower: strings.poolLower,
    upper: strings.poolUpper,
    digits: strings.poolDigits,
    symbols: strings.poolSymbols,
    other: strings.poolOther,
  };
  const present = POOL_KEYS.filter((key) => pools[key]).map(
    (key) => names[key]
  );
  return present.length > 0 ? present.join(", ") : strings.poolNone;
}

function Meter({
  rating,
  strings,
}: {
  rating: number;
  strings: PasswordStrings;
}) {
  const label = strings[RATING_KEYS[rating]];
  return (
    <div className="space-y-2">
      <p className="font-semibold text-2xl text-foreground">{label}</p>
      <div
        aria-label={t(strings.meterLabel, { rating, label })}
        className="flex gap-1.5"
        role="img"
      >
        {[1, 2, 3, 4].map((step) => (
          <span
            className={`h-2.5 flex-1 rounded-full ${step <= rating ? RATING_TONES[rating] : "bg-muted"}`}
            key={step}
          />
        ))}
      </div>
      <p className="text-muted-foreground text-sm">
        {strings[RATING_NOTES[rating]]}
      </p>
    </div>
  );
}

function Report({
  password,
  year,
  dictionaries,
  strings,
}: {
  password: string;
  year: number;
  dictionaries: PasswordDictionaries;
  strings: PasswordStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const estimate = estimateStrength(password, year, dictionaries);
  const bits = (value: number) => {
    const rounded = Math.round(value);
    return tPlural(strings.bits, locale, rounded, { count: fmt(rounded) });
  };
  return (
    <>
      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.verdict}</h2>
        <Meter rating={estimate.rating} strings={strings} />
        {estimate.truncated ? (
          <p className={HINT}>
            {t(strings.truncated, { count: fmt(MAX_LENGTH) })}
          </p>
        ) : null}
      </section>
      <StatGrid>
        <StatCard label={strings.length} value={fmt(estimate.length)} />
        <StatCard
          label={strings.pools}
          value={poolText(estimate.pools, strings)}
        />
        <StatCard
          label={strings.guesses}
          value={guessText(estimate.log10Guesses, fmt)}
        />
        <StatCard label={strings.poolBits} value={bits(estimate.poolBits)} />
        <StatCard
          emphasis
          label={strings.patternBits}
          value={bits(estimate.bits)}
        />
      </StatGrid>
      <p className={HINT}>{strings.entropyNote}</p>
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.crackHeading}</h2>
        <CrackTable seconds={estimate.seconds} strings={strings} />
        <p className={HINT}>{strings.crackNote}</p>
      </section>
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.patternsHeading}</h2>
        <PatternList sequence={estimate.sequence} strings={strings} />
      </section>
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.suggestionsHeading}</h2>
        <Suggestions ids={suggestionsFor(estimate)} strings={strings} />
      </section>
    </>
  );
}

export default function PasswordStrengthCheckerWidget() {
  const strings = useToolUi(PASSWORD_STRENGTH_CHECKER_STRINGS);
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [dictionaries, setDictionaries] = useState<PasswordDictionaries | null>(
    null
  );
  const [year, setYear] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setYear(new Date().getFullYear());
    import("@/lib/tool-kit/ciphers/password-lists")
      .then((lists) => {
        if (alive) {
          setDictionaries(
            buildDictionaries({
              passwords: lists.COMMON_PASSWORDS,
              english: lists.ENGLISH_WORDS,
              firstNames: lists.FIRST_NAMES,
              surnames: lists.SURNAMES,
            })
          );
        }
      })
      .catch(() => {
        if (alive) {
          setFailed(true);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  const ready = dictionaries !== null && year !== null;
  let status: string | null = null;
  if (password === "") {
    status = strings.empty;
  } else if (!(ready || failed)) {
    status = strings.loading;
  }

  return (
    <>
      <section className="space-y-1 rounded-lg border border-green-600/40 bg-green-600/5 p-4">
        <p className="font-semibold text-foreground text-sm">
          {strings.privacyTitle}
        </p>
        <p className="text-muted-foreground text-sm">{strings.privacy}</p>
      </section>
      <section className="space-y-2">
        <label className={LABEL} htmlFor="psc-password">
          {strings.password}
        </label>
        <div className="flex gap-2">
          <input
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            className={INPUT_MONO}
            id="psc-password"
            onChange={(event) => setPassword(event.target.value)}
            placeholder={strings.placeholder}
            spellCheck={false}
            type={visible ? "text" : "password"}
            value={password}
          />
          <button
            aria-label={visible ? strings.hideLabel : strings.showLabel}
            aria-pressed={visible}
            className={BUTTON_SECONDARY}
            onClick={() => setVisible(!visible)}
            type="button"
          >
            {visible ? strings.hide : strings.show}
          </button>
        </div>
      </section>
      <div className="space-y-5">
        <div aria-live="polite">
          {status ? <Notice>{status}</Notice> : null}
        </div>
        {failed && password !== "" ? (
          <ErrorNotice>{strings.loadFailed}</ErrorNotice>
        ) : null}
        {ready && password !== "" ? (
          <Report
            dictionaries={dictionaries}
            password={password}
            strings={strings}
            year={year}
          />
        ) : null}
      </div>
    </>
  );
}
